import { useEffect, useState } from 'react';
import { PanelBody } from '@/components/ui';
import { api, ApiError } from '@/lib/api';
import {
  CLAWBACK_KIND_LABELS,
  UNASSIGNED_CLAWBACKS_AMOUNT_NOTE,
  UNASSIGNED_CLAWBACKS_HEADING,
  strandedRemainderSentence,
} from '@/lib/commissions';
import { formatDecimalString } from '@/lib/format';
import type { CommissionStrandedRemainder, CommissionUnassignedLeadClawbacks } from '@/lib/types';

/**
 * A row that needs attention: a surface row with a warning left edge, rather
 * than a fully tinted box. The sentence carries the meaning; the edge is the
 * second signal, never the only one.
 */
const WARNING_ROW =
  'rounded-md border border-l-[3px] border-border border-l-warning bg-surface px-2.5 py-1.5 text-xs text-fg-secondary [overflow-wrap:anywhere]';

/* ---------------------------------------------------------------------------
 * CLAWBACK REMAINDERS THIS RUN CANNOT RECOVER — owner decision 2026-09-24.
 *
 * A remainder keeps carrying until recovered (migration 227), but a run reaches
 * a lead only through an ASSIGNMENT in its period. A lead with a remainder on
 * their latest approved statement and no assignment here is passed over
 * silently by the compute; `GET /commissions/runs/:id/unrecovered` names them,
 * and this renders it.
 *
 * ⛔ NEVER SUMMED. One line per person, each in its own currency: a total across
 *    people would add money in different currencies, and the server already
 *    reads each person's ONE latest statement because the column is cumulative.
 *
 * ⚠️ AN EMPTY LIST RENDERS NOTHING. It is the ordinary state, and a block
 *    reading "nobody" on every run is the block that teaches somebody to stop
 *    reading it. A FAILED read renders its failure — it is not evidence that
 *    nobody is stranded.
 *
 * ✅ 2026-09-24, owner decision: THE SAME READ ALSO CARRIES `unassignedClawbacks`
 *    — clawbacks on revenue a lead was PAID commission on, where that lead holds
 *    no assignment in this run's period. They are found and listed PER LEAD,
 *    with one total PER CURRENCY, and never deducted: there is no statement here
 *    to deduct them from. ⛔ The amounts are the documents' own money, not
 *    commission. An absent key is an older server — it renders nothing, never
 *    "nobody".
 * ------------------------------------------------------------------------- */

export interface StrandedRemaindersPanelProps {
  runId: number;
  /** Bumped by the page after a write, so the list is re-read with the run. */
  reloadKey?: string | number;
}

export function StrandedRemaindersPanel({ runId, reloadKey = 0 }: StrandedRemaindersPanelProps) {
  const [rows, setRows] = useState<CommissionStrandedRemainder[] | null>(null);
  const [unassigned, setUnassigned] = useState<CommissionUnassignedLeadClawbacks[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    api
      .listCommissionStrandedRemainders(runId, controller.signal)
      .then((answer) => {
        if (controller.signal.aborted) return;
        setRows(answer.data);
        // `?? null`, never `?? []` — an absent key is a server that did not look.
        setUnassigned(answer.unassignedClawbacks ?? null);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (controller.signal.aborted) return;
        setRows(null);
        setUnassigned(null);
        setError(
          caught instanceof ApiError
            ? caught.message
            : 'Could not check for clawback remainders this run cannot recover',
        );
      });
    return () => controller.abort();
  }, [runId, reloadKey]);

  if (error !== null) {
    return (
      <PanelBody className="border-b border-border">
        <p className="m-0 text-xs text-danger" role="alert">
          Could not check for clawback remainders this run cannot recover. {error}
        </p>
      </PanelBody>
    );
  }

  const remainders = rows ?? [];
  const leads = unassigned ?? [];
  if (remainders.length === 0 && leads.length === 0) return null;

  return (
    <PanelBody className="border-b border-border">
      {remainders.length > 0 && (
        <>
          <p className="m-0 text-xs font-semibold text-fg-secondary">
            Clawback not recoverable in this run ({remainders.length})
          </p>
          <ul className="mt-2 m-0 flex list-none flex-col gap-1 p-0">
            {remainders.map((row) => (
              <li
                key={row.sourceStatementId}
                className={WARNING_ROW}
              >
                <span className="font-medium text-fg">{row.user.name ?? row.user.email}</span>
                {' — '}
                <span className="tabular-nums text-fg">
                  {formatDecimalString(row.amount)} {row.currency}
                </span>{' '}
                {strandedRemainderSentence(row.sourcePeriod.name)}
                {` (run ${row.sourceRunId})`}
              </li>
            ))}
          </ul>
        </>
      )}

      {leads.length > 0 && (
        <div className={remainders.length > 0 ? 'mt-3' : undefined}>
          <p className="m-0 text-xs font-semibold text-fg-secondary">
            {UNASSIGNED_CLAWBACKS_HEADING} ({leads.length})
          </p>
          <p className="m-0 mt-0.5 text-xs text-fg-muted">{UNASSIGNED_CLAWBACKS_AMOUNT_NOTE}</p>
          <ul className="mt-2 m-0 flex list-none flex-col gap-1.5 p-0">
            {leads.map((lead) => (
              <li
                key={lead.user.id}
                className={WARNING_ROW}
              >
                <span className="font-medium text-fg">{lead.user.name ?? lead.user.email}</span>
                {' — '}
                {/* ⛔ One total per currency, joined for reading and never added. */}
                {lead.totals.map((total, index) => (
                  <span key={total.currency} className="tabular-nums">
                    {index > 0 ? ' · ' : ''}
                    {formatDecimalString(total.amount)} {total.currency}
                  </span>
                ))}
                <ul className="mt-1 m-0 flex list-none flex-col gap-0.5 p-0 pl-3">
                  {lead.items.map((item) => (
                    <li key={`${item.kind}:${item.creditNoteId ?? item.invoiceId}`}>
                      {CLAWBACK_KIND_LABELS[item.kind] ?? item.kind}
                      {item.invoiceId !== null ? ` #${item.invoiceId}` : ''}
                      {item.creditNoteId !== null ? ` #${item.creditNoteId}` : ''}
                      {item.description ? ` — ${item.description}` : ''}
                      {' · '}
                      <span className="tabular-nums">
                        {formatDecimalString(item.amount)} {item.currency}
                      </span>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      )}
    </PanelBody>
  );
}
